// Copyright (c) Ping. All rights reserved.

import { decodeEnvelope } from '@ping/ui/src/transport-mls/cborEnvelope';

import { log } from '@core/log/log';
import { get } from '@core/network/api';
import { db } from '@core/persistence/db';
import { pushChatRouteId } from '@shell/hosts/chatRouteUrl';

export interface PushWakePayload {
  readonly delivery_id?: string;
  readonly kind?: string;
  readonly is_video?: boolean;
}

interface InboxFetchResponse {
  readonly envelope: string;
}

function urlBase64ToBytes(b64: string): Uint8Array {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    out[i] = raw.charCodeAt(i);
  }
  return out;
}

function bytesToUrlBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const i of bytes) {
    binary += String.fromCharCode(i);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function resolveChatRouteId(conversationId: string): Promise<string> {
  const conv = await db.conversations.get(conversationId);
  if (conv?.kind === 'direct' && conv.peerAccountId) {
    return conv.peerAccountId;
  }
  return conversationId;
}

function conversationRouteIdFromEnvelope(envelopeB64: string): string | null {
  try {
    const wire = urlBase64ToBytes(envelopeB64.trim());
    const decoded = decodeEnvelope(wire);
    if (decoded.conversation_id.length !== 16) {
      return null;
    }
    return bytesToUrlBase64(decoded.conversation_id);
  } catch {
    return null;
  }
}

/**
 * Handles a minimal push payload: fetch the envelope by delivery id so the
 * device inbox advances, then open the matching chat when known locally.
 */
export async function wakeFromPushDelivery(payload: PushWakePayload): Promise<void> {
  const deliveryId = payload.delivery_id?.trim();
  if (deliveryId === undefined || deliveryId.length !== 32) {
    log.warn({ event: 'push.wake.skip', reason: 'invalid_delivery_id' });
    return;
  }

  log.info({
    event: 'push.wake',
    deliveryIdPrefix: deliveryId.slice(0, 8),
    kind: payload.kind,
  });

  try {
    const body = await get<InboxFetchResponse>(`/v1/inbox/by-delivery-id/${deliveryId}`);
    log.info({ event: 'push.wake.fetch.ok', deliveryIdPrefix: deliveryId.slice(0, 8) });

    const localConversationId = conversationRouteIdFromEnvelope(body.envelope);
    if (localConversationId !== null) {
      const routeId = await resolveChatRouteId(localConversationId);
      pushChatRouteId(routeId);
      log.info({
        event: 'push.wake.navigate',
        deliveryIdPrefix: deliveryId.slice(0, 8),
      });
    }
  } catch (err) {
    log.warn({
      event: 'push.wake.fetch.failed',
      deliveryIdPrefix: deliveryId.slice(0, 8),
      err: (err as Error).message,
    });
  }
}
